import { decodeToken } from 'utils/auth';

const TOKEN_KEY = 'jwt_token';
const CLUB_ROLE_KEY = 'club_role';
const CLUB_PERMISSION_LEVEL_KEY = 'club_permission_level';
const SELECTED_CLUB_ID_KEY = 'selected_club_id';

const PORTAL_ROLE_KEY = 'portal_role';
const USER_INFO_KEY = 'user_info';
const USER_EMAIL_KEY = 'user_email';
const SESSION_EXPIRED_KEY = 'session_expired';

export const setToken = (storage, token) => {
  storage.setItem(TOKEN_KEY, token);
};

export const getToken = () => {
  return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
};

export const removeToken = () => {
  localStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
};

export const setClubRole = (clubRole) => {
  localStorage.setItem(CLUB_ROLE_KEY, clubRole);
};

export const getClubRole = () => {
  return (
    localStorage.getItem(CLUB_ROLE_KEY) || sessionStorage.getItem(CLUB_ROLE_KEY)
  );
};

export const removeClubRole = () => {
  localStorage.removeItem(CLUB_ROLE_KEY);
  sessionStorage.removeItem(CLUB_ROLE_KEY);
};

/**
 * @deprecated
 */
export const setPortalRole = (portalRole) => {
  localStorage.setItem(PORTAL_ROLE_KEY, portalRole);
};

/**
 * @deprecated
 */
export const getPortalRole = () => {
  return (
    localStorage.getItem(PORTAL_ROLE_KEY) ||
    sessionStorage.getItem(PORTAL_ROLE_KEY)
  );
};

export const setUserPermissionLevel = (permission_level) => {
  localStorage.setItem(CLUB_PERMISSION_LEVEL_KEY, permission_level);
};

export const getUserPermissionLevel = () => {
  return (
    localStorage.getItem(CLUB_PERMISSION_LEVEL_KEY) ||
    sessionStorage.getItem(CLUB_PERMISSION_LEVEL_KEY)
  );
};

export const removeUserPermissionLevel = () => {
  localStorage.removeItem(CLUB_PERMISSION_LEVEL_KEY);
  sessionStorage.removeItem(CLUB_PERMISSION_LEVEL_KEY);
};

export const removePortalRole = () => {
  localStorage.removeItem(PORTAL_ROLE_KEY);
  sessionStorage.removeItem(PORTAL_ROLE_KEY);
};

export const setUserInfo = (storage, userInfo) => {
  storage.setItem(USER_INFO_KEY, userInfo);
};

export const getUserInfo = () => {
  return (
    localStorage.getItem(USER_INFO_KEY) || sessionStorage.getItem(USER_INFO_KEY)
  );
};

export const removeUserInfo = () => {
  localStorage.removeItem(USER_INFO_KEY);
  sessionStorage.removeItem(USER_INFO_KEY);
};

export const setUserEmail = (storage, userEmail) => {
  storage.setItem(USER_EMAIL_KEY, userEmail);
};

export const getUserEmail = () => {
  return (
    localStorage.getItem(USER_EMAIL_KEY) ||
    sessionStorage.getItem(USER_EMAIL_KEY)
  );
};

export const removeUserEmail = () => {
  localStorage.removeItem(USER_EMAIL_KEY);
  sessionStorage.removeItem(USER_EMAIL_KEY);
};

// Remembers the club the user last had active, so they return to it next time
// instead of always defaulting to their first club. Stored alongside the token:
// a "Remember me" login keeps it in localStorage (survives across sessions on
// this device); a session-only login keeps it in sessionStorage (cleared with
// the tab), so a shared computer doesn't leave the club selection behind.
export const setSelectedClubId = (clubId) => {
  if (!clubId) return;
  const storage = localStorage.getItem(TOKEN_KEY) ? localStorage : sessionStorage;
  storage.setItem(SELECTED_CLUB_ID_KEY, clubId);
};

export const getSelectedClubId = () => {
  return (
    localStorage.getItem(SELECTED_CLUB_ID_KEY) ||
    sessionStorage.getItem(SELECTED_CLUB_ID_KEY)
  );
};

export const removeSelectedClubId = () => {
  localStorage.removeItem(SELECTED_CLUB_ID_KEY);
  sessionStorage.removeItem(SELECTED_CLUB_ID_KEY);
};

// Removes the token and every piece of derived identity from BOTH storages. The one way to end a session — used by
// logout, by the api layer when the server rejects our token, and by persistSession before starting a new session.
export const clearSession = () => {
  removeToken();
  removeUserInfo();
  removeUserEmail();
  removePortalRole();
  removeClubRole();
  removeUserPermissionLevel();
  removeSelectedClubId();
};

// Called by the api layer when the server 401s an authenticated request: the session is dead and a full-page redirect
// to /login follows, which wipes all in-memory state — so the "why" has to survive in sessionStorage for the Login
// page to explain.
export const markSessionExpired = () => {
  sessionStorage.setItem(SESSION_EXPIRED_KEY, '1');
};

// One-shot read by the Login page: whether we arrived because the session expired. Clears the flag so a manual
// refresh of /login doesn't repeat the message.
export const consumeSessionExpiredFlag = () => {
  const expired = sessionStorage.getItem(SESSION_EXPIRED_KEY) !== null;
  sessionStorage.removeItem(SESSION_EXPIRED_KEY);
  return expired;
};

// The single way to start a session from a login / onboarding response. Used by BOTH the Login page and the public
// registration dialog so their behaviour can never drift.
//
// `remember` decides persistence: true → localStorage (stays signed in across sessions); false → sessionStorage
// (cleared when the tab closes). The public dialog always passes false — persistence is opt-in, and that opt-in lives
// only on the Login page's "Remember me".
//
// It ALWAYS clears the prior token + identity from BOTH storages first. This is the important bit: getToken() reads
// localStorage before sessionStorage, so a leftover localStorage token would otherwise silently override a fresh
// session-only login (and survive a tab close). Clearing both means exactly one token exists and the latest login wins.
// `email` is the address the user signed in with — the token/response doesn't carry it.
export const persistSession = (data, { remember = false, email = '' } = {}) => {
  const storage = remember ? localStorage : sessionStorage;

  // Clean slate across both storages — token, identity, and any stale role/permission/club from a previous login.
  clearSession();

  setToken(storage, data.token);
  setUserInfo(
    storage,
    JSON.stringify({ firstName: data.firstName, lastName: data.lastName })
  );
  setUserEmail(storage, email);
  try {
    setPortalRole(decodeToken(data.token).userRole);
  } catch {
    /* the token always decodes here; ignore if it somehow doesn't */
  }
  const clubPermission = data.clubPermissions?.[0] ?? null;
  if (clubPermission) {
    setClubRole(clubPermission.clubRole);
    setUserPermissionLevel(clubPermission.userPermissionLevel);
  }
};
